// Code map display preferences, kept per browser. Layout, spacing and line style are shared with
// the model diagrams (diagram/settings.ts).
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PropertyMode } from '../diagram/graph';

export type CodeToggle = 'insideFile' | 'access' | 'guesses' | 'uses';

/** How the code map draws test files: left out, faint, or like the rest. */
export type TestFiles = 'hide' | 'dim' | 'show';

interface CodeSettings {
  /** `all` members, `keys`: members with a line ("Linked"), `none`: types only. */
  members: PropertyMode;
  /** Endpoint map: `all` reached members, `none` only the types they are in. */
  endpointMembers: PropertyMode;
  /** Test map: the same choice. */
  testMembers: PropertyMode;
  /** Code map: test files (hidden at first: tests call nearly everything). */
  tests: TestFiles;
  insideFile: boolean;
  access: boolean;
  guesses: boolean;
  uses: boolean;
  setMembers: (mode: PropertyMode) => void;
  setEndpointMembers: (mode: PropertyMode) => void;
  setTestMembers: (mode: PropertyMode) => void;
  setTests: (tests: TestFiles) => void;
  toggle: (key: CodeToggle) => void;
}

export const useCodeSettings = create<CodeSettings>()(
  persist(
    (set) => ({
      members: 'keys',
      endpointMembers: 'all',
      testMembers: 'all',
      tests: 'hide',
      insideFile: false,
      access: false,
      guesses: true,
      uses: true,
      setMembers: (members) => set({ members }),
      setEndpointMembers: (endpointMembers) => set({ endpointMembers }),
      setTestMembers: (testMembers) => set({ testMembers }),
      setTests: (tests) => set({ tests }),
      toggle: (key) => set((s) => ({ [key]: !s[key] })),
    }),
    { name: 'mv-codemap', version: 1 },
  ),
);
